import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Building2, CreditCard, Hash, MapPin } from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './BankDrawer.module.css';

const BankDrawer = ({ isOpen, onClose, onSave, bank }) => {
  const [formData, setFormData] = useState({
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    branch_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchBankDetail = async () => {
      if (bank && isOpen) {
        setIsFetching(true);
        try {
          // Pre-populate with existing data from props while fetching
          setFormData({
            bank_name: bank.bank_name || '',
            bank_account_number: bank.bank_account_number || '',
            ifsc_code: bank.ifsc_code || '',
            branch_name: bank.branch_name || ''
          });

          const response = await api.get(`/bank-accounts/${bank.id}`);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              bank_name: data.bank_name || '',
              bank_account_number: data.bank_account_number || '',
              ifsc_code: data.ifsc_code || '',
              branch_name: data.branch_name || ''
            });
          }
        } catch (error) {
          console.error('Error fetching bank details:', error);
          toast.error('Failed to fetch latest bank details');
        } finally {
          setIsFetching(false);
        }
      } else {
        setFormData({
          bank_name: '',
          bank_account_number: '',
          ifsc_code: '',
          branch_name: ''
        });
        setIsFetching(false);
      }
    };

    fetchBankDetail();
  }, [bank, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bank_name.trim()) {
      toast.error('Bank name is required');
      return;
    }
    if (!formData.bank_account_number.trim()) {
      toast.error('Account number is required');
      return;
    }
    if (!formData.ifsc_code.trim()) {
      toast.error('IFSC code is required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (bank) {
        response = await api.put(`/bank-accounts/${bank.id}`, formData);
      } else {
        response = await api.post('/bank-accounts', formData);
      }

      if (response.data.success) {
        toast.success(bank ? 'Bank account updated successfully' : 'Bank account created successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving bank:', error);
      toast.error(error.response?.data?.message || 'Failed to save bank account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{bank ? 'Edit Bank Account' : 'Add Bank Account'}</h2>
            <p>{bank ? 'Update details for this bank account.' : 'Register a new bank account for office use.'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching bank details...</span>
              </div>
            ) : (
              <>
                <div className={styles['form-group']}>
                  <label htmlFor="bank_name">
                    <Building2 size={16} />
                    <span>Bank Name <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="bank_name"
                    name="bank_name"
                    placeholder="e.g. HDFC Bank, SBI"
                    value={formData.bank_name}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                </div>

                <div className={styles['form-group']}>
                  <label htmlFor="bank_account_number">
                    <CreditCard size={16} />
                    <span>Account Number <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="bank_account_number"
                    name="bank_account_number"
                    placeholder="Enter account number"
                    value={formData.bank_account_number}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles['form-group']}>
                  <label htmlFor="ifsc_code">
                    <Hash size={16} />
                    <span>IFSC Code <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="ifsc_code"
                    name="ifsc_code"
                    placeholder="e.g. HDFC0001234"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles['form-group']}>
                  <label htmlFor="branch_name">
                    <MapPin size={16} />
                    <span>Branch Name</span>
                  </label>
                  <input
                    type="text"
                    id="branch_name"
                    name="branch_name"
                    placeholder="Enter branch name"
                    value={formData.branch_name}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{bank ? 'Update Bank' : 'Save Bank'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankDrawer;
